import { useState, useCallback, useEffect } from 'react';
import { substituteTokensWithValues } from '../constants/tokenDefinitions';
import { validateIVRPrompt } from '../utils/promptValidation';

interface UseIVRPromptModalProps {
  isOpen: boolean;
  queueId: string;
  token: string;
  onClose: () => void;
  setRowData: (queueId: string, newPrompt: string) => void;
}

const useIVRPromptModal = ({
  isOpen,
  queueId,
  token,
  onClose,
  setRowData
}: UseIVRPromptModalProps) => {
  const [prompt, setPrompt] = useState<string>("Loading...");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showTokenInput, setShowTokenInput] = useState<boolean>(false);
  const [selectedToken, setSelectedToken] = useState<string>("");
  const [tokenInputValue, setTokenInputValue] = useState<string>("");

  // Handle token insertion
  const insertToken = useCallback((tokenText: string, requiresInput: boolean = false) => {
    if (requiresInput) {
      // For calculation tokens, show input modal
      setSelectedToken(tokenText);
      setTokenInputValue("");
      setShowTokenInput(true);
    } else {
      // For simple tokens, insert directly
      const textarea = document.getElementById('ivr-prompt-textarea') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = textarea.value;
        const newText = currentText.substring(0, start) + tokenText + currentText.substring(end);
        
        setPrompt(newText);
        
        // Set cursor position after the inserted token
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + tokenText.length, start + tokenText.length);
        }, 0);
      }
    }
  }, []);

  // Handle token input confirmation
  const handleTokenInputConfirm = useCallback((value: string) => {
    const fullToken = selectedToken.replace('| minutesPerCall>', `| ${value}>`);
    const textarea = document.getElementById('ivr-prompt-textarea') as HTMLTextAreaElement;
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = textarea.value;
      const newText = currentText.substring(0, start) + fullToken + currentText.substring(end);
      
      setPrompt(newText);
      
      // Set cursor position after the inserted token
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + fullToken.length, start + fullToken.length);
      }, 0);
    }
    
    setShowTokenInput(false);
    setSelectedToken("");
  }, [selectedToken]);

  const handleTokenInputCancel = useCallback(() => {
    setShowTokenInput(false);
    setSelectedToken("");
  }, []);

  // Handle autoplay functionality
  const handleAutoplay = useCallback(async () => {
    if (!prompt || prompt === "Loading...") return;
    
    setIsPlaying(true);
    try {
      const data = JSON.stringify({
        prompt: {
          mode: 'TextToSpeech',
          text: prompt,
        },
      });

      const res = await fetch(`https://platform.ringcentral.com/restapi/v1.0/account/~/ivr-menus/${queueId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: data,
      });
      
      if (res.ok) {
        console.log('Autoplay updated successfully');
      } else {
        throw new Error('Failed to update autoplay');
      }
    } catch (err) {
      setError(`Failed to update autoplay: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsPlaying(false);
    }
  }, [prompt, queueId, token]);

  // Fetches and sets the old Prompt on load.
  useEffect(() => {
    if (!isOpen || !queueId) return;

    const fetchPrompt = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/prompt/${queueId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch prompt');
        }

        const data = await response.json();
        setPrompt(data.text || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setPrompt(''); // Reset on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompt();
  }, [isOpen, queueId, token]);

  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value);
    setError(null); // Clear error when user starts typing
    setWarning(null); // Clear warning when user starts typing
    
    // Real-time validation feedback (but don't block typing)
    if (value && value !== "Loading...") {
      const validation = validateIVRPrompt(value);
      if (!validation.isValid) {
        setError(`Validation issues: ${validation.errors.join(', ')}`);
      } else if (validation.warnings && validation.warnings.length > 0) {
        setWarning(validation.warnings.join(', '));
      }
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!prompt.trim()) {
      setError('Prompt cannot be empty');
      return;
    }
    
    // Validate tokens before showing confirmation
    const validation = validateIVRPrompt(prompt);
    if (!validation.isValid) {
      setError(`Token validation failed: ${validation.errors.join(', ')}`);
      return;
    }
    
    // Show warnings but allow submission
    if (validation.warnings && validation.warnings.length > 0) {
      setWarning(validation.warnings.join(', '));
    }
    
    setShowConfirmation(true);
  }, [prompt]);

  const handleConfirmSubmit = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/prompt/${queueId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: prompt,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update prompt');
      }

      // Update parent state
      setRowData(queueId, prompt);

      // Close modal and confirmation
      setShowConfirmation(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setIsLoading(false);
    }
  }, [prompt, queueId, token, onClose, setRowData]);

  const handleClose = useCallback(() => {
    if (isLoading) return; // Prevent closing during API calls
    
    setShowConfirmation(false);
    setError(null);
    onClose();
  }, [isLoading, onClose]);

  const handleConfirmationCancel = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setError(null);
      setWarning(null);
      setShowConfirmation(false);
      setIsPlaying(false);
    }
  }, [isOpen]);

  return {
    prompt,
    isLoading,
    showConfirmation,
    error,
    warning,
    isPlaying,
    showTokenInput,
    insertToken,
    handleTokenInputConfirm,
    handleTokenInputCancel,
    handleAutoplay,
    handlePromptChange,
    handleSubmit,
    handleConfirmSubmit,
    handleClose,
    handleConfirmationCancel
  };
};

export default useIVRPromptModal;